import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Link, Navigate } from 'react-router-dom';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { FuelTypeActions } from 'redux/actions/settings/FuelTypeActions';
import { MachineActions } from 'redux/actions/settings/MachineActions';
import { MachineHistoryActions } from 'redux/actions/machines/MachineHistoryActions';

import { FuelType } from 'models/FuelType';
import { LoginUser } from 'models/LoginUser';
import { Machine } from 'models/Machine';
import { MachineDefect } from 'models/MachineDefect';
import { MachineHistory } from 'models/MachineHistory';
import { StoreModel } from 'models/StoreModel';
import { CurrentSubscription } from 'models/SubscriptionPlan';
import { Translations } from 'models/Translations';
import { Unit } from 'models/Unit';

import { Translate } from 'infrastructure/mapping/Translate';
import { litersOnReceipt, parseNumbers, quantityFirst, readText } from 'infrastructure/Ocr';
import { PageContentHeader } from 'app/components/common/PageContentHeader';
import Translator from 'app/components/common/Translator';
import { withRouter } from "app/components/common/WithRouter";

interface Props {
    fetchFuelTypes: typeof FuelTypeActions.fetchFuelTypes;
    fetchMachines: typeof MachineActions.fetchMachines;
    addMachineHistory: typeof MachineHistoryActions.addMachineHistory;
    editMachineHistory: typeof MachineHistoryActions.editMachineHistory;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    fuelTypes: FuelType[];
    loginUser: LoginUser;
    machines: Machine[];
    machineHistories: MachineHistory[];
    processing: boolean;
    match: UrlParams;
    currentSubscription: CurrentSubscription | null;
    translations: Translations;
}

type Errors = { [fieldId: string]: string };

type State = {
    report?: MachineHistory;
    // existing defects the user removed; sent blank so the backend deletes them
    removedDefects: MachineDefect[];
    errors: Errors;
    // per field: numbers recognized on a photo, or the reading state
    ocr: { [field: string]: number[] | 'busy' | 'failed' };
}

interface UrlParams {
    params: {
        id?: string;
    }
}

type Option<T> = { value: T, label: string };

class DailyReport extends React.Component<Props, State> {
    public readonly state: State = {
        removedDefects: [],
        errors: {},
        ocr: {}
    }

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.fuelTypes) {
                this.props.fetchFuelTypes(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machines && this.props.fuelTypes && this.props.machineHistories
    }

    defectsEnabled() {
        return !!this.props.currentSubscription?.subscriptionPlan.defectsEnabled;
    }

    t(text: string) {
        return Translate.text(this.props.translations, text);
    }

    isEdit() {
        return !!this.props.match.params.id;
    }

    // the draft is derived from the store until the first edit, then it lives in state
    report(): MachineHistory | undefined {
        if (this.state.report)
            return this.state.report;
        if (!this.isEdit()) {
            const assigned = Machine.assignedTo(this.props.machines, this.props.loginUser.userData?.id);
            // a new report starts from the machine's last known service life
            return assigned ? { ...MachineHistory.emptyDailyReport(), machine: assigned, serviceLife: assigned.serviceLife } : MachineHistory.emptyDailyReport();
        }
        const mh = this.props.machineHistories.find(mh => mh.id === parseInt(this.props.match.params.id!));
        if (!mh)
            return undefined;
        const report = MachineHistory.editDailyReport(mh);
        return { ...report, machineDefects: report.machineDefects?.length ? report.machineDefects : [MachineDefect.empty()] };
    }

    update(changes: Partial<MachineHistory>, clearErrors: string[] = []) {
        const errors = { ...this.state.errors };
        clearErrors.forEach(e => delete errors[e]);
        this.setState({ report: { ...this.report()!, ...changes }, errors });
    }

    selectedMachine(report: MachineHistory) {
        return this.props.machines.find(m => m.id === report.machine.id);
    }

    selectedFuelType(report: MachineHistory) {
        return this.props.fuelTypes.find(ft => ft.id === report.fuelStop?.fuelType.id);
    }

    selectMachine(machine: Machine) {
        const changes: Partial<MachineHistory> = { machine };
        // a new report starts from the machine's last known service life
        if (!this.isEdit())
            changes.serviceLife = machine.serviceLife;
        this.update(changes, ['machine', 'serviceLife']);
    }

    setDefect(idx: number, changes: Partial<MachineDefect>) {
        const defects = [...this.report()!.machineDefects!];
        defects[idx] = { ...defects[idx], ...changes };
        this.update({ machineDefects: defects }, ['defect.' + idx]);
    }

    addDefect() {
        const defects = [...this.report()!.machineDefects!, MachineDefect.empty()];
        this.update({ machineDefects: defects });
        // focus the new row so keyboard users can type right away
        setTimeout(() => document.getElementById('defect.' + (defects.length - 1))?.focus());
    }

    removeDefect(idx: number) {
        const defects = [...this.report()!.machineDefects!];
        const [removed] = defects.splice(idx, 1);
        if (removed.id > 0) {
            this.setState(prev => ({ removedDefects: [...prev.removedDefects, { ...removed, defect: '', description: '' }] }));
        }
        this.update({ machineDefects: defects.length ? defects : [MachineDefect.empty()] }, Object.keys(this.state.errors).filter(e => e.startsWith('defect.')));
    }

    validate(report: MachineHistory): Errors {
        const errors: Errors = {};
        const machine = this.selectedMachine(report);
        if (!machine) {
            errors.machine = this.t("Select a machine");
        }
        if (!report.date) {
            errors.date = this.t("Select a date");
        }
        if (isNaN(report.serviceLife) || report.serviceLife < 0) {
            errors.serviceLife = this.t("Enter the service life");
        } else if (machine && !this.isEdit() && report.serviceLife < machine.serviceLife) {
            errors.serviceLife = this.t("New service life must be greater or equal to the current one");
        }
        const quantity = report.fuelStop?.quantity ?? 0;
        if (quantity > 0 && !this.selectedFuelType(report)) {
            errors.fuelType = this.t("Select a fuel type");
        }
        if (this.selectedFuelType(report) && !(quantity > 0)) {
            errors.quantity = this.t("Enter a quantity");
        }
        report.machineDefects?.forEach((d, idx) => {
            // the backend drops defects without a title, so a lone description would be lost
            if (!d.defect.trim() && d.description.trim()) {
                errors['defect.' + idx] = this.t("Enter a short title for the defect");
            }
        });
        return errors;
    }

    validateField(fieldId: string) {
        const error = this.validate(this.report()!)[fieldId];
        const errors = { ...this.state.errors };
        if (error)
            errors[fieldId] = error;
        else
            delete errors[fieldId];
        this.setState({ errors });
    }

    submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const report = this.report()!;
        const errors = this.validate(report);
        this.setState({ errors });
        const firstError = Object.keys(errors)[0];
        if (firstError) {
            document.getElementById(firstError)?.focus();
            return;
        }
        const payload: MachineHistory = {
            ...report,
            machine: this.selectedMachine(report)!,
            machineDefects: [
                ...report.machineDefects!.filter(d => d.id > 0 || d.defect.trim()),
                ...this.state.removedDefects
            ],
        };
        const save = this.isEdit() ? this.props.editMachineHistory : this.props.addMachineHistory;
        save(this.props.loginUser.token, payload);
    }

    // average of the machine's last 5 fuel stops of the report's fuel type, or of the type it was fuelled with last
    averageQuantity(report: MachineHistory): number | undefined {
        const stops = this.props.machineHistories
            .filter(mh => mh.id !== report.id && mh.machine.id === report.machine.id && !mh.archivedDate && mh.fuelStop?.fuelType && !mh.fuelStop.archivedDate && mh.fuelStop.quantity > 0)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const fuelTypeId = this.selectedFuelType(report)?.id ?? stops[0]?.fuelStop!.fuelType.id;
        const last5 = stops.filter(mh => mh.fuelStop!.fuelType.id === fuelTypeId).slice(0, 5);
        return last5.length ? last5.reduce((sum, mh) => sum + mh.fuelStop!.quantity, 0) / last5.length : undefined;
    }

    async readPhoto(field: string, apply: (n: number) => void, file?: File) {
        if (!file)
            return;
        this.setState(prev => ({ ocr: { ...prev.ocr, [field]: 'busy' } }));
        let text = '';
        try {
            text = await readText(file);
        } catch (e) {
            console.error(e);
        }
        let numbers = parseNumbers(text);
        if (field === 'serviceLife') {
            // a dashboard shows many numbers; the reading closest above the last service life is the likely one
            const last = this.selectedMachine(this.report()!)?.serviceLife ?? 0;
            const distance = (n: number) => n >= last ? n - last : Infinity;
            numbers.sort((a, b) => distance(a) - distance(b));
        } else if (field === 'quantity') {
            // a machine's tank has a fixed size, so a fill-up is close to its usual quantity
            const average = this.averageQuantity(this.report()!);
            numbers = average ? numbers.sort((a, b) => Math.abs(a - average) - Math.abs(b - average)) : quantityFirst(numbers);
            // litres printed on a receipt beat both guesses
            const liters = litersOnReceipt(text);
            if (liters)
                numbers = [liters, ...numbers.filter(n => n !== liters)];
        }
        // the most likely number goes straight into the field, the others stay as alternatives
        if (numbers.length)
            apply(numbers[0]);
        this.setState(prev => ({ ocr: { ...prev.ocr, [field]: numbers.length ? numbers.slice(1) : 'failed' } }));
    }

    photoReader(field: string, apply: (n: number) => void) {
        const ocr = this.state.ocr[field];
        const pick = (n: number) => {
            apply(n);
            this.setState(prev => ({ ocr: { ...prev.ocr, [field]: [] } }));
        };
        return (
            <div className="c-form__ocr">
                <input
                    id={field + '-photo'}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={e => { this.readPhoto(field, apply, e.currentTarget.files?.[0]); e.currentTarget.value = ''; }}
                />
                <button type="button" className="c-button c-button--ghost" onClick={() => document.getElementById(field + '-photo')?.click()} disabled={ocr === 'busy'} aria-busy={ocr === 'busy'}>
                    <FontAwesomeIcon icon={faCamera} className="c-button__icon c-button__icon--left" />
                    <Translator text={ocr === 'busy' ? "Reading photo..." : "Read from photo"} />
                </button>
                <div className="c-form__ocr-results" role="status">
                    {ocr === 'failed' && <span className="c-form__hint"><Translator text="No numbers recognized" /></span>}
                    {Array.isArray(ocr) && ocr.length > 0 && <span className="c-form__hint"><Translator text="Alternatives" />:</span>}
                    {Array.isArray(ocr) && ocr.map(n =>
                        <button key={n} type="button" className="c-button c-button--ghost" onClick={() => pick(n)} aria-label={this.t("Use") + ' ' + n}>{n}</button>
                    )}
                </div>
            </div>
        );
    }

    unitSuffix(unit?: Unit | null) {
        return unit?.short ? <span className="c-form__suffix" aria-hidden="true">{unit.short}</span> : null;
    }

    fieldError(fieldId: string) {
        const error = this.state.errors[fieldId];
        return error ? <div className="c-form__validation" id={fieldId + '-error'} role="alert">{error}</div> : null;
    }

    errorProps(fieldId: string, hintId?: string) {
        const invalid = !!this.state.errors[fieldId];
        return {
            'aria-invalid': invalid,
            'aria-describedby': invalid ? fieldId + '-error' : hintId,
        };
    }

    render() {
        const title = this.isEdit() ? "Edit daily report" : "Daily report";
        if (!this.loggedIn()) {
            return <Navigate replace to="/account/login" />;
        }
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text={title} />
                    <div className="c-card" aria-busy="true"><Translator text="Loading" /></div>
                </div>
            )
        }
        const report = this.report();
        if (!report) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text={title} />
                    <div className="c-card">
                        <p><Translator text="Daily report not found" /></p>
                        <Link className="c-button c-button--ghost" to="/Machines/histories"><Translator text="Back to history" /></Link>
                    </div>
                </div>
            )
        }

        const machine = this.selectedMachine(report);
        const fuelType = this.selectedFuelType(report);
        const serviceLifeUnit = machine?.machineType?.serviceLifeUnit;
        const machineOptions: Option<Machine>[] = this.props.machines
            .filter(m => !m.archivedDate || m.id === machine?.id)
            .map(m => ({ value: m, label: m.identification ? m.name + ' · ' + m.identification : m.name }));
        const fuelOptions: Option<FuelType>[] = this.props.fuelTypes
            .filter(ft => !ft.archivedDate || ft.id === fuelType?.id)
            .map(ft => ({ value: ft, label: ft.name }));
        const selectProps = {
            classNamePrefix: "c-select",
            placeholder: this.t("Select..."),
            noOptionsMessage: () => this.t("No options"),
        };

        return (
            <div className="c-page__content">
                <PageContentHeader text={title} />
                <form className="c-form c-form--wide" onSubmit={(e) => this.submit(e)} noValidate>
                    <fieldset className="c-card c-form__section">
                        <legend className="c-form__section-title"><Translator text="Machine" /></legend>
                        <div className="c-form__grid">
                            <div className="c-form__field c-form__field--full">
                                <label className="c-form__label c-form__label--required" htmlFor="machine"><Translator text="Machine" /></label>
                                <Select<Option<Machine>>
                                    {...selectProps}
                                    inputId="machine"
                                    className={"c-form__select" + (this.state.errors.machine ? " c-form__select--invalid" : "")}
                                    options={machineOptions}
                                    value={machineOptions.find(o => o.value.id === machine?.id) ?? null}
                                    onChange={o => o && this.selectMachine(o.value)}
                                    aria-invalid={!!this.state.errors.machine}
                                    aria-errormessage="machine-error"
                                    autoFocus={!this.isEdit()}
                                />
                                {this.fieldError('machine')}
                            </div>
                            <div className="c-form__field">
                                <label className="c-form__label c-form__label--required" htmlFor="date"><Translator text="Date" /></label>
                                <ReactDatePicker
                                    id="date"
                                    className="c-form__control"
                                    selected={report.date}
                                    onChange={(date: Date | null) => this.update({ date: date ?? undefined } as Partial<MachineHistory>, ['date'])}
                                    dateFormat="dd.MM.yyyy HH:mm"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeCaption={this.t("Time")}
                                    maxDate={new Date()}
                                    locale="de" // todo, make this react to set language
                                    autoComplete="off"
                                    ariaInvalid={this.state.errors.date ? 'true' : 'false'}
                                    ariaDescribedBy={this.state.errors.date ? 'date-error' : undefined}
                                />
                                {this.fieldError('date')}
                            </div>
                            <div className="c-form__field">
                                <label className="c-form__label c-form__label--required" htmlFor="serviceLife"><Translator text="Service Life" /></label>
                                <div className="c-form__input-group">
                                    <input
                                        id="serviceLife"
                                        className="c-form__control"
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="any"
                                        value={isNaN(report.serviceLife) ? '' : report.serviceLife}
                                        onChange={e => this.update({ serviceLife: e.currentTarget.valueAsNumber }, ['serviceLife'])}
                                        onBlur={() => this.validateField('serviceLife')}
                                        {...this.errorProps('serviceLife', machine ? 'serviceLife-hint' : undefined)}
                                    />
                                    {this.unitSuffix(serviceLifeUnit)}
                                </div>
                                {this.photoReader('serviceLife', n => this.update({ serviceLife: n }, ['serviceLife']))}
                                {this.fieldError('serviceLife') ?? (machine &&
                                    <div className="c-form__hint" id="serviceLife-hint">
                                        {this.t("Last recorded")}: {machine.serviceLife}{serviceLifeUnit?.short ? ' ' + serviceLifeUnit.short : ''}
                                    </div>
                                )}
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className="c-card c-form__section">
                        <legend className="c-form__section-title">
                            <Translator text="Fuel" /> <span className="c-form__optional"><Translator text="optional" /></span>
                        </legend>
                        <div className="c-form__grid">
                            <div className="c-form__field">
                                <label className="c-form__label" htmlFor="fuelType"><Translator text="Fuel Type" /></label>
                                <Select<Option<FuelType>>
                                    {...selectProps}
                                    inputId="fuelType"
                                    className={"c-form__select" + (this.state.errors.fuelType ? " c-form__select--invalid" : "")}
                                    options={fuelOptions}
                                    value={fuelOptions.find(o => o.value.id === fuelType?.id) ?? null}
                                    onChange={o => this.update({ fuelStop: { ...report.fuelStop!, fuelType: o ? o.value : FuelType.empty() } }, ['fuelType', 'quantity'])}
                                    isClearable
                                    aria-invalid={!!this.state.errors.fuelType}
                                    aria-errormessage="fuelType-error"
                                />
                                {this.fieldError('fuelType')}
                            </div>
                            <div className="c-form__field">
                                <label className="c-form__label" htmlFor="quantity"><Translator text="Quantity" /></label>
                                <div className="c-form__input-group">
                                    <input
                                        id="quantity"
                                        className="c-form__control"
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="any"
                                        value={report.fuelStop?.quantity || ''}
                                        onChange={e => this.update({ fuelStop: { ...report.fuelStop!, quantity: e.currentTarget.valueAsNumber || 0 } }, ['quantity', 'fuelType'])}
                                        onBlur={() => this.validateField('quantity')}
                                        {...this.errorProps('quantity')}
                                    />
                                    {this.unitSuffix(fuelType?.unit)}
                                </div>
                                {this.photoReader('quantity', n => this.update({ fuelStop: { ...this.report()!.fuelStop!, quantity: n } }, ['quantity', 'fuelType']))}
                                {this.fieldError('quantity')}
                            </div>
                        </div>
                    </fieldset>

                    {this.defectsEnabled() && <fieldset className="c-card c-form__section">
                        <legend className="c-form__section-title">
                            <Translator text="Defects" /> <span className="c-form__optional"><Translator text="optional" /></span>
                        </legend>
                        <ul className="c-form__list">
                            {report.machineDefects!.map((d, idx) => (
                                <li className="c-form__list-item" key={idx}>
                                    <div className="c-form__field">
                                        <label className="c-form__label" htmlFor={'defect.' + idx}><Translator text="Defect" /></label>
                                        <input
                                            id={'defect.' + idx}
                                            className="c-form__control"
                                            value={d.defect}
                                            onChange={e => this.setDefect(idx, { defect: e.currentTarget.value })}
                                            onBlur={() => this.validateField('defect.' + idx)}
                                            autoComplete="off"
                                            {...this.errorProps('defect.' + idx)}
                                        />
                                        {this.fieldError('defect.' + idx)}
                                    </div>
                                    <div className="c-form__field">
                                        <label className="c-form__label" htmlFor={'description.' + idx}><Translator text="Description" /></label>
                                        <textarea
                                            id={'description.' + idx}
                                            className="c-form__control c-form__textarea"
                                            rows={2}
                                            value={d.description}
                                            onChange={e => this.setDefect(idx, { description: e.currentTarget.value })}
                                        />
                                    </div>
                                    {(report.machineDefects!.length > 1 || d.defect || d.description) &&
                                        <button
                                            type="button"
                                            className="c-button c-button--ghost c-form__list-remove"
                                            onClick={() => this.removeDefect(idx)}
                                            aria-label={this.t("Remove defect") + (d.defect ? ': ' + d.defect : '')}
                                            title={this.t("Remove defect")}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                    }
                                    <div className="c-form__field c-form__field--full c-form__field--inline">
                                        <input
                                            type="checkbox"
                                            id={'outOfOrder.' + idx}
                                            className="c-form__control"
                                            checked={!!d.outOfOrderFlag}
                                            onChange={e => this.setDefect(idx, { outOfOrderFlag: e.currentTarget.checked })}
                                        />
                                        <label className="c-form__label" htmlFor={'outOfOrder.' + idx}><Translator text="Machine out of order" /></label>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <button type="button" className="c-button c-button--ghost" onClick={() => this.addDefect()}>
                            <FontAwesomeIcon icon={faPlus} className="c-button__icon c-button__icon--left" />
                            <Translator text="Add defect" />
                        </button>
                    </fieldset>}

                    <div className="c-form__actions">
                        <Link className="c-button c-button--ghost" to="/Machines/histories"><Translator text="Cancel" /></Link>
                        <button className="c-button c-button--primary" type="submit" disabled={this.props.processing} aria-busy={this.props.processing}>
                            {this.props.processing ? <div className="c-button__loader" /> : <Translator text="Save" />}
                        </button>
                    </div>
                </form>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        fuelTypes: state.fuelTypes,
        machineHistories: state.machineHistories,
        processing: state.processingRequest,
        currentSubscription: state.currentSubscription,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchFuelTypes: bindActionCreators(FuelTypeActions.fetchFuelTypes, dispatch),
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        addMachineHistory: bindActionCreators(MachineHistoryActions.addMachineHistory, dispatch),
        editMachineHistory: bindActionCreators(MachineHistoryActions.editMachineHistory, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
    };
}
export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(DailyReport));
